import React, { useRef, useState } from "react";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Tooltip } from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseIcon from "@mui/icons-material/Close";
import { exportChartPng } from "utils/charts/exportChartPng";
import { useSnackbar } from "utils/hooks/useSnackbar";

interface Props {
    // ダウンロードファイル名（.png は自動付与）。
    filename: string;
    // 全画面ダイアログのタイトル。
    title?: string;
    // 描画済みチャート（presentational）。全画面では同じ children を再描画する。
    // fetch を持たない presentational なチャートを渡すこと（再 fetch を避けるため）。
    children: React.ReactNode;
}

// チャートに「PNG 保存 / 全画面表示」の操作を付与する共有ツールバー。
// 取得済みの描画 DOM を読むだけで、データ・API・系列定義・取得タイミングは変えない。
// 全画面は children を Dialog に再描画する（fetch hook を持たない presentational 前提）。
export const ChartActions: React.FC<Props> = ({ filename, title, children }) => {
    // チャート本体だけを包む ref。インライン側でツールバーのアイコン <svg> を
    // 誤って画像化しないよう、children を独立した要素に閉じて保存対象にする。
    const chartRef = useRef<HTMLDivElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const { showError } = useSnackbar();

    const savePng = async (host: HTMLElement | null) => {
        if (!host) return;
        try {
            await exportChartPng(host, filename);
        } catch (e) {
            showError(e instanceof Error ? e.message : "PNG 出力に失敗しました");
        }
    };

    return (
        <Box sx={{ position: "relative" }}>
            <Box sx={{ position: "absolute", top: 4, right: 4, zIndex: 2, display: "flex", gap: 0.5 }}>
                <Tooltip title="PNG 画像を保存">
                    <IconButton size="small" aria-label="PNG 画像を保存" onClick={() => savePng(chartRef.current)}>
                        <ImageOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="全画面表示">
                    <IconButton size="small" aria-label="全画面表示" onClick={() => setOpen(true)}>
                        <FullscreenIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            <Box ref={chartRef}>{children}</Box>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <span>{title ?? "グラフ"}</span>
                    <Box>
                        <Tooltip title="PNG 画像を保存">
                            <IconButton aria-label="PNG 画像を保存" onClick={() => savePng(dialogRef.current)}>
                                <ImageOutlinedIcon />
                            </IconButton>
                        </Tooltip>
                        <IconButton aria-label="閉じる" onClick={() => setOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box ref={dialogRef}>{children}</Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};
